import React, { useEffect, useState } from "react";
import { Plus, Trash2, ChevronDown, ChevronUp, Sparkles, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { PageTitle, Field, TextInput, TextArea } from "./shared";

const HERITAGE_STATUSES = [
  { value: "new", label: "NEW", color: "#EC2675" },
  { value: "packing", label: "PACKING", color: "#FF7A3D" },
  { value: "shipped", label: "SHIPPED", color: "#4ECDE9" },
  { value: "completed", label: "COMPLETED", color: "#1F8A50" },
  { value: "cancelled", label: "CANCELLED", color: "#6E2470" },
];

const SOURCES = [
  { value: "manual", label: "MANUAL" },
  { value: "website", label: "WEBSITE" },
  { value: "custom", label: "CUSTOM" },
  { value: "hq", label: "HQ" },
  { value: "bloom_club", label: "BLOOM CLUB" },
];

const TypeBadge = ({ type }) => (
  <span className={`px-2 py-0.5 rounded-full text-[9px] uppercase font-bold tracking-[0.1em] ${type === "tuber" ? "bg-forest/15 text-forest" : "bg-sky/25 text-sky-deep"}`}>
    {type}
  </span>
);

const NewOrderForm = ({ varieties, onCreated, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", source: "manual", total: "", notes: "" });
  const [rows, setRows] = useState([{ variety_id: varieties[0]?.id || "", quantity: 1 }]);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/heritage-orders", {
        customer: { name: form.name, email: form.email, phone: form.phone, address: form.address },
        source: form.source,
        items: rows.filter((r) => r.variety_id).map((r) => ({ variety_id: r.variety_id, quantity: Number(r.quantity) || 1 })),
        total: Number(form.total) || 0,
        notes: form.notes,
      });
      toast.success(`Heritage order ${data.job_number} created`);
      onCreated(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create order");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-warmwhite border-2 border-forest/30 rounded-[1.5rem] p-6 mb-6 space-y-4" data-testid="new-heritage-order-form">
      <div className="flex items-center justify-between">
        <h2 className="font-serif-display uppercase text-plum-deep text-xl">NEW HERITAGE ORDER</h2>
        <button type="button" onClick={onClose} className="text-plum-deep/40 hover:text-plum-deep" aria-label="Close"><X className="w-5 h-5" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="CUSTOMER NAME *"><TextInput required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="heritage-customer-name" /></Field>
        <Field label="EMAIL"><TextInput type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="heritage-customer-email" /></Field>
        <Field label="PHONE"><TextInput value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
        <Field label="ADDRESS"><TextInput value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
        <Field label="ORDER SOURCE">
          <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="heritage-source" className="w-full rounded-full px-5 py-2.5 bg-cream border border-plum-deep/15 text-sm focus:outline-none focus:border-magenta">
            {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="ORDER TOTAL (NZD)"><TextInput type="number" step="0.5" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} data-testid="heritage-total" /></Field>
      </div>

      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-plum-deep/60 mb-2 font-semibold">VARIETIES</p>
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={i} className="flex items-center gap-3">
              <select
                value={row.variety_id}
                onChange={(e) => setRows((r) => r.map((x, j) => (j === i ? { ...x, variety_id: e.target.value } : x)))}
                data-testid={`heritage-item-variety-${i}`}
                className="flex-1 rounded-full px-5 py-2.5 bg-cream border border-plum-deep/15 text-sm focus:outline-none focus:border-magenta"
              >
                {varieties.map((v) => <option key={v.id} value={v.id}>{v.name} ({v.type.toUpperCase()})</option>)}
              </select>
              <TextInput type="number" min={1} value={row.quantity} onChange={(e) => setRows((r) => r.map((x, j) => (j === i ? { ...x, quantity: e.target.value } : x)))} data-testid={`heritage-item-qty-${i}`} className="w-24" />
              {rows.length > 1 && (
                <button type="button" onClick={() => setRows((r) => r.filter((_, j) => j !== i))} className="text-plum-deep/40 hover:text-red-600" aria-label="Remove row"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setRows((r) => [...r, { uid: crypto.randomUUID(), variety_id: varieties[0]?.id || "", quantity: 1 }])} data-testid="heritage-add-item" className="mt-3 inline-flex items-center gap-2 text-forest text-xs uppercase tracking-[0.14em] font-semibold hover:underline">
          <Plus className="w-3.5 h-3.5" /> ADD ANOTHER VARIETY
        </button>
      </div>

      <Field label="NOTES"><TextArea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
      <button type="submit" disabled={busy} data-testid="heritage-create-order" className="inline-flex items-center gap-2 rounded-full bg-forest text-cream px-7 py-3 text-xs uppercase tracking-[0.16em] font-semibold hover:bg-plum disabled:opacity-50">
        {busy ? "CREATING…" : "CREATE ORDER"}
      </button>
    </form>
  );
};

const OrderRow = ({ order, onChanged, onDeleted }) => {
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);

  const patch = async (body, msg) => {
    await api.patch(`/admin/heritage-orders/${order.id}`, body);
    onChanged({ ...order, ...body });
    toast.success(msg);
  };

  const generateTags = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/admin/heritage-orders/${order.id}/generate-tags`);
      onChanged({ ...order, tags_generated: true, print_job_id: data.id });
      toast.success(`Tags sent to Etchery print queue — job ${data.job_number}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not generate tags");
    } finally {
      setGenerating(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${order.job_number}?`)) return;
    await api.delete(`/admin/heritage-orders/${order.id}`);
    onDeleted(order.id);
    toast.success("Order deleted");
  };

  const statusOpt = HERITAGE_STATUSES.find((s) => s.value === order.status) || HERITAGE_STATUSES[0];
  const src = SOURCES.find((s) => s.value === order.source);

  return (
    <div className="bg-warmwhite border border-plum-deep/10 rounded-[1.25rem] overflow-hidden" data-testid={`heritage-row-${order.job_number}`}>
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 px-5 py-4 text-left" data-testid={`heritage-toggle-${order.job_number}`}>
        <span className="font-semibold text-sm w-28 shrink-0">{order.job_number}</span>
        <span className="flex-1 text-sm truncate">{order.customer?.name}</span>
        <span className="hidden sm:inline-block px-2.5 py-1 rounded-full bg-forest/10 text-forest text-[10px] uppercase font-bold tracking-[0.1em]">{src?.label || order.source}</span>
        <span className="text-sm font-semibold">${order.total?.toFixed(2)}</span>
        {order.tags_generated && <Sparkles className="w-4 h-4 text-magenta shrink-0" title="Tags generated" />}
        <span className="px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.12em] font-bold text-cream" style={{ background: statusOpt.color }}>{statusOpt.label}</span>
        {open ? <ChevronUp className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-plum-deep/10 pt-4 grid md:grid-cols-2 gap-5" data-testid={`heritage-detail-${order.job_number}`}>
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-2">VARIETIES</p>
            <ul className="space-y-2">
              {order.items?.map((it, i) => (
                <li key={i} className="flex items-center gap-3 bg-cream rounded-xl px-3.5 py-2.5">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{it.variety_name}</p>
                    <TypeBadge type={it.type} />
                  </div>
                  <span className="text-sm text-plum-deep/70">×{it.quantity}</span>
                </li>
              ))}
            </ul>
            {order.notes && <p className="mt-3 text-xs text-plum-deep/70 bg-lemon/20 rounded-xl p-3">{order.notes}</p>}
            <div className="mt-3 text-sm text-plum-deep/70">
              {order.customer?.email && <p>{order.customer.email}</p>}
              {order.customer?.phone && <p>{order.customer.phone}</p>}
              {order.customer?.address && <p>{order.customer.address}</p>}
            </div>
          </div>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <label>
                <span className="block text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-1">STATUS</span>
                <select value={order.status} onChange={(e) => patch({ status: e.target.value }, "Status updated")} data-testid={`heritage-status-${order.job_number}`} className="rounded-full px-4 py-2 bg-cream border border-plum-deep/15 text-sm focus:outline-none focus:border-magenta">
                  {HERITAGE_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </label>
              <label>
                <span className="block text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-1">PAYMENT</span>
                <select value={order.payment_status} onChange={(e) => patch({ payment_status: e.target.value }, "Payment updated")} className="rounded-full px-4 py-2 bg-cream border border-plum-deep/15 text-sm focus:outline-none focus:border-magenta">
                  <option value="unpaid">UNPAID</option>
                  <option value="paid">PAID</option>
                </select>
              </label>
            </div>

            <div className="bg-cream rounded-2xl p-4">
              <p className="text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-2">PLANT TAGS</p>
              {order.tags_generated ? (
                <p className="flex items-center gap-2 text-sm text-forest font-medium" data-testid={`heritage-tags-done-${order.job_number}`}>
                  <CheckCircle2 className="w-4 h-4" /> Tags generated — in the Etchery print queue as {order.job_number}
                </p>
              ) : (
                <>
                  <p className="text-xs text-plum-deep/60 mb-3">One click sends variety names, quantities, customer and job number straight to The Etchery. No double entry.</p>
                  <button onClick={generateTags} disabled={generating} data-testid={`heritage-generate-tags-${order.job_number}`} className="inline-flex items-center gap-2 rounded-full bg-magenta text-cream px-5 py-2.5 text-xs uppercase tracking-[0.16em] font-semibold hover:bg-plum disabled:opacity-50">
                    <Sparkles className="w-4 h-4" /> {generating ? "GENERATING…" : "GENERATE TAGS"}
                  </button>
                </>
              )}
            </div>

            <button onClick={remove} className="inline-flex items-center gap-2 rounded-full bg-cream border border-plum-deep/20 text-plum-deep px-4 py-2 text-xs uppercase tracking-[0.14em] font-semibold hover:text-red-600" data-testid={`heritage-delete-${order.job_number}`}>
              <Trash2 className="w-3.5 h-3.5" /> DELETE ORDER
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const HeritageOrders = () => {
  const [orders, setOrders] = useState(null);
  const [varieties, setVarieties] = useState([]);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/admin/heritage-orders"), api.get("/admin/varieties")])
      .then(([o, v]) => { setOrders(o.data); setVarieties(v.data); })
      .catch(() => { setOrders([]); });
  }, []);

  return (
    <div data-testid="console-heritage-orders">
      <PageTitle kicker="HERITAGE DAHLIAS" title="HERITAGE ORDERS">
        <button onClick={() => setShowForm((v) => !v)} data-testid="heritage-new-order-btn" className="inline-flex items-center gap-2 rounded-full bg-forest text-cream px-6 py-3 text-xs uppercase tracking-[0.16em] font-semibold hover:bg-plum">
          <Plus className="w-4 h-4" /> NEW ORDER
        </button>
      </PageTitle>

      {showForm && varieties.length > 0 && (
        <NewOrderForm
          varieties={varieties}
          onClose={() => setShowForm(false)}
          onCreated={(order) => { setOrders((l) => [order, ...(l || [])]); setShowForm(false); }}
        />
      )}
      {showForm && varieties.length === 0 && (
        <p className="mb-6 text-sm text-plum-deep/60">Add varieties in the Variety Database first.</p>
      )}

      {orders === null ? (
        <p className="text-plum-deep/60">Loading…</p>
      ) : orders.length === 0 ? (
        <p className="text-plum-deep/60" data-testid="heritage-empty">No heritage orders yet — create your first one.</p>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <OrderRow
              key={o.id}
              order={o}
              onChanged={(u) => setOrders((l) => l.map((x) => (x.id === u.id ? u : x)))}
              onDeleted={(id) => setOrders((l) => l.filter((x) => x.id !== id))}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HeritageOrders;
